import type { Building, BuildingId, Citizen, CitizenId, GridCoord, GridCell } from '../../types/core';
import { coordToWorld, worldToCoord } from '../../utils/grid';
import { citizenId } from '../../utils/id';
import { clamp, hashString, mulberry32, pick } from '../../utils/random';
import { getBuildingDefinition } from '../../world/buildingDefinitions';
import { citizenName } from '../../world/names';
import { findRoadPath } from './pathfinding';

export interface CitizenUpdateResult {
  readonly citizens: readonly Citizen[];
  readonly buildings: readonly Building[];
}

function buildingCoord(building: Building): GridCoord {
  return building.cells[0] ?? { x: 0, z: 0 };
}

function completedBuildings(buildings: readonly Building[]): readonly Building[] {
  return buildings.filter((building) => building.stage === 'completed');
}

function desiredDestination(
  citizen: Citizen,
  buildings: readonly Building[],
  minuteOfDay: number
): { readonly destinationId?: BuildingId; readonly activity: Citizen['activity'] } {
  const completed = completedBuildings(buildings);
  const home = citizen.homeId ? completed.find((building) => building.id === citizen.homeId) : undefined;
  const work = citizen.workplaceId ? completed.find((building) => building.id === citizen.workplaceId) : undefined;
  const shops = completed.filter((building) => building.zone === 'commercial');
  const parks = completed.filter((building) => building.zone === 'park');
  const rng = mulberry32(citizen.routineSeed + Math.floor(minuteOfDay / 180));

  if (minuteOfDay < 6 * 60 || minuteOfDay > 22 * 60) {
    return { destinationId: home?.id, activity: 'Sleeping' };
  }

  if (minuteOfDay >= 7.5 * 60 && minuteOfDay <= 16.5 * 60 && work) {
    return { destinationId: work.id, activity: 'Working' };
  }

  if (minuteOfDay >= 17 * 60 && minuteOfDay <= 19.5 * 60 && shops.length > 0) {
    return { destinationId: pick(shops, rng).id, activity: 'Shopping' };
  }

  if (minuteOfDay > 12 * 60 && minuteOfDay <= 21.5 * 60 && parks.length > 0) {
    return { destinationId: pick(parks, rng).id, activity: 'Relaxing' };
  }

  return { destinationId: home?.id, activity: 'At Home' };
}

function assignWorkplace(
  citizen: Citizen,
  buildings: readonly Building[],
  workerCounts: ReadonlyMap<BuildingId, number>,
  rng: () => number
): Citizen {
  if (citizen.workplaceId) {
    return citizen;
  }

  const workplaces = completedBuildings(buildings)
    .filter((building) => building.zone === 'commercial' || building.zone === 'workspace' || building.zone === 'park')
    .filter((building) => (workerCounts.get(building.id) ?? 0) < getBuildingDefinition(building.kind).jobs);

  if (workplaces.length === 0 || rng() > 0.55) {
    return citizen;
  }

  return {
    ...citizen,
    workplaceId: pick(workplaces, rng).id
  };
}

function spawnCitizens(
  citizens: readonly Citizen[],
  buildings: readonly Building[],
  cells: readonly GridCell[],
  size: number,
  minuteOfDay: number,
  rng: () => number
): Citizen[] {
  const currentByHome = new Map<BuildingId, number>();

  for (const citizen of citizens) {
    if (citizen.homeId) {
      currentByHome.set(citizen.homeId, (currentByHome.get(citizen.homeId) ?? 0) + 1);
    }
  }

  const spawned: Citizen[] = [];
  const completedHomes = completedBuildings(buildings).filter((building) => building.zone === 'residential');

  for (const home of completedHomes) {
    const definition = getBuildingDefinition(home.kind);
    const current = currentByHome.get(home.id) ?? 0;
    const openSlots = definition.capacity - current;

    if (openSlots <= 0) {
      continue;
    }

    const shouldSpawn = minuteOfDay > 6 * 60 && minuteOfDay < 22 * 60 && (rng() < 0.16 || citizens.length < 4);
    if (!shouldSpawn) {
      continue;
    }

    const id = citizenId(`${home.id}-${current}-${Math.floor(rng() * 100000)}`);
    const position = coordToWorld(buildingCoord(home), size);
    const routineSeed = hashString(id);
    const newCitizen: Citizen = {
      id,
      name: citizenName(rng),
      age: 18 + Math.floor(rng() * 62),
      homeId: home.id,
      happiness: 62 + Math.round(rng() * 28),
      activity: 'At Home',
      position,
      path: [worldToCoord(position, size)],
      pathIndex: 0,
      routineSeed
    };

    spawned.push(newCitizen);
    currentByHome.set(home.id, current + 1);
  }

  return [...citizens, ...spawned].slice(0, Math.max(citizens.length, 1800));
}

function moveCitizen(
  citizen: Citizen,
  buildings: readonly Building[],
  cells: readonly GridCell[],
  size: number,
  minuteOfDay: number,
  deltaMinutes: number
): Citizen {
  const desired = desiredDestination(citizen, buildings, minuteOfDay);
  const destination = desired.destinationId
    ? buildings.find((building) => building.id === desired.destinationId)
    : undefined;
  const currentCoord = worldToCoord(citizen.position, size);
  const needsPath = desired.destinationId !== citizen.destinationId || citizen.path.length === 0;
  const path = needsPath && destination ? findRoadPath(currentCoord, buildingCoord(destination), cells, size) : citizen.path;
  const basePathIndex = needsPath ? 0 : citizen.pathIndex;
  const targetCoord = path[Math.min(basePathIndex, Math.max(0, path.length - 1))] ?? currentCoord;
  const target = coordToWorld(targetCoord, size);
  const speed = desired.activity === 'Commuting' ? 0.18 : 0.12;
  const step = speed * deltaMinutes;
  const dx = target.x - citizen.position.x;
  const dz = target.z - citizen.position.z;
  const distance = Math.hypot(dx, dz);
  const reached = distance < 0.035 || step >= distance;
  const pathIndex = reached ? Math.min(basePathIndex + 1, Math.max(0, path.length - 1)) : basePathIndex;
  const moving = pathIndex < path.length - 1;
  const position = reached
    ? target
    : {
        x: citizen.position.x + (dx / Math.max(distance, 0.001)) * step,
        z: citizen.position.z + (dz / Math.max(distance, 0.001)) * step
      };
  const activity = moving && desired.activity !== 'Sleeping' ? 'Commuting' : desired.activity;
  const happinessDelta = activity === 'Relaxing' ? 0.03 : activity === 'Commuting' ? -0.01 : 0.006;

  return {
    ...citizen,
    activity,
    destinationId: desired.destinationId,
    position,
    path,
    pathIndex,
    happiness: clamp(citizen.happiness + happinessDelta * deltaMinutes, 8, 100)
  };
}

export function updateCitizens(
  citizens: readonly Citizen[],
  buildings: readonly Building[],
  cells: readonly GridCell[],
  size: number,
  minuteOfDay: number,
  deltaMinutes: number,
  rng: () => number
): CitizenUpdateResult {
  const withNewResidents = spawnCitizens(citizens, buildings, cells, size, minuteOfDay, rng);
  const workerCounts = new Map<BuildingId, number>();

  for (const citizen of withNewResidents) {
    if (citizen.workplaceId) {
      workerCounts.set(citizen.workplaceId, (workerCounts.get(citizen.workplaceId) ?? 0) + 1);
    }
  }

  const moved = withNewResidents
    .map((citizen) => assignWorkplace(citizen, buildings, workerCounts, rng))
    .map((citizen) => moveCitizen(citizen, buildings, cells, size, minuteOfDay, deltaMinutes));

  const residents = new Map<BuildingId, CitizenId[]>();
  const workers = new Map<BuildingId, CitizenId[]>();
  const visitors = new Map<BuildingId, CitizenId[]>();

  for (const citizen of moved) {
    if (citizen.homeId) {
      residents.set(citizen.homeId, [...(residents.get(citizen.homeId) ?? []), citizen.id]);
    }
    if (citizen.workplaceId) {
      workers.set(citizen.workplaceId, [...(workers.get(citizen.workplaceId) ?? []), citizen.id]);
    }
    if (citizen.destinationId && citizen.activity !== 'At Home' && citizen.activity !== 'Sleeping') {
      visitors.set(citizen.destinationId, [...(visitors.get(citizen.destinationId) ?? []), citizen.id]);
    }
  }

  const updatedBuildings = buildings.map((building) => {
    const mood: Building['mood'] =
      building.stage !== 'completed'
        ? 'Busy'
        : building.zone === 'commercial' && (visitors.get(building.id)?.length ?? 0) > 6
          ? 'Busy'
          : building.zone === 'park'
            ? 'Happy'
            : 'Calm';
    const operatingStatus: Building['operatingStatus'] =
      building.stage !== 'completed'
        ? 'Building'
        : building.zone === 'commercial' && (minuteOfDay < 7 * 60 || minuteOfDay > 22 * 60)
          ? 'Closed'
          : 'Open';

    return {
      ...building,
      residents: residents.get(building.id) ?? [],
      workers: workers.get(building.id) ?? [],
      visitors: visitors.get(building.id) ?? [],
      mood,
      operatingStatus
    };
  });

  return {
    citizens: moved,
    buildings: updatedBuildings
  };
}
